'use client';

import { AtClickable, AtText, AtIcon } from '@/components/atoms';
import {
  EClickableTarget,
  EClickableTypes,
  EClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { EIcons } from '@/components/atoms/at-icon/icons';
import { useLocation } from '@/context/locationContext';
import { LoginModalContext } from '@/context/loginModalContext';
import {
  useSetLocationModal,
  ESetLocationType,
} from '@/context/setLocationContext';
import { useContext, useEffect } from 'react';
import { IActionColumnProps, IClickableLink } from './or-footer.types';

export const FooterActions = ({
  actionButtons,
  socialLinks,
}: IActionColumnProps) => {
  const { authCTA, cta1, cta2, phoneNumber } = actionButtons;
  const { currentBuildStudio } = useLocation();
  const { handleOpen } = useSetLocationModal();
  const { buildLoginUrl } = useContext(LoginModalContext);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Replaces phone number in footer
      window?.Invoca?.PNAPI?.run();
    }
  }, []);

  const renderButton = (cta: IClickableLink) => (
    <AtClickable
      label={cta.label}
      type={cta.type}
      href={cta.href}
      target={cta.target || EClickableTarget.SELF}
      variant={cta.variant || EClickableVariants.PRIMARY}
      outline={cta.outline}
      interactLocation="footer"
    />
  );

  return (
    <div className="flex flex-1 flex-col gap-[3.75rem]">
      <div className="flex flex-col items-center gap-5">
        {renderButton(cta1)}
        {renderButton(cta2)}
      </div>

      <div className="flex flex-col items-center gap-4 md:gap-[1.125rem]">
        <AtText type="span">
          <span className="sr-only">Phone Number:</span>
          <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
        </AtText>

        <AtClickable
          label={authCTA.label}
          href={currentBuildStudio?.Id ? authCTA.href : undefined}
          type={authCTA.type}
          onClick={() => {
            if (!currentBuildStudio?.Id) {
              handleOpen({
                type: ESetLocationType.LOGIN,
                redirect: buildLoginUrl({
                  LoginAction: ESetLocationType.LOGIN,
                  StudioId: String(currentBuildStudio?.Id),
                }),
              });
            }
          }}
          variant={authCTA.variant}
          target={authCTA.target || EClickableTarget.SELF}
          interactLocation="footer"
        />
      </div>

      <div className="mb-3 flex justify-center gap-8 text-2xl md:gap-[1.375rem] lg:mb-0">
        {socialLinks.map(({ href, target, icon }, i) => (
          <AtClickable
            className="p-0 text-26"
            key={`social-link-${i}`}
            href={href.startsWith('http') ? href : `https://${href}`}
            target={target || EClickableTarget.BLANK}
            rel="noopener noreferrer"
            interactLocation="footer"
            ariaLabel={icon}
            gtmLabel={icon}
            type={EClickableTypes.ICON}
          >
            <AtIcon
              icon={icon as EIcons}
              color="var(--color-ui-fill-secondary)"
            />
          </AtClickable>
        ))}
      </div>
    </div>
  );
};
